import { supabase } from '../lib/supabase';

/**
 * Service functions for iRefer.org.uk Integration
 * Handles credential management, guideline matching, and access logging
 */

// =================================================================
// CREDENTIAL MANAGEMENT
// =================================================================

/**
 * Save iRefer credentials for user
 * @param {string} userId - User ID
 * @param {string} email - iRefer email
 * @param {string} password - iRefer password (will be encrypted)
 * @param {boolean} autoLogin - Enable auto-login
 * @returns {Promise<Object>} Update result
 */
export const saveIreferCredentials = async (userId, email, password, autoLogin = true) => {
  try {
    // In a production environment, password encryption would happen server-side
    // For this implementation, we'll store a flag indicating credentials are set
    // Note: Actual password encryption requires backend infrastructure
    
    const { data, error } = await supabase?.from('user_profiles')?.update({
        irefer_email: email,
        irefer_password_encrypted: 'ENCRYPTED_' + btoa(email), // Placeholder for encrypted password
        irefer_auto_login: autoLogin,
        irefer_verified: true
      })?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving iRefer credentials:', error);
    throw error;
  }
};

/**
 * Get iRefer credentials status for user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Credential status
 */
export const getIreferCredentials = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('irefer_email, irefer_auto_login, irefer_verified')?.eq('id', userId)?.single();

    if (error) throw error;
    
    return {
      email: data?.irefer_email || '',
      autoLogin: data?.irefer_auto_login || false,
      verified: data?.irefer_verified || false
    };
  } catch (error) {
    console.error('Error fetching iRefer credentials:', error);
    throw error;
  }
};

/**
 * Remove iRefer credentials for user
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Success status
 */
export const removeIreferCredentials = async (userId) => {
  try {
    const { error } = await supabase?.from('user_profiles')?.update({
        irefer_email: null,
        irefer_password_encrypted: null,
        irefer_auto_login: false,
        irefer_verified: false
      })?.eq('id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing iRefer credentials:', error);
    throw error;
  }
};

// =================================================================
// GUIDELINE MAPPINGS
// =================================================================

/**
 * Get iRefer guideline by code
 * @param {string} ireferCode - iRefer guideline code
 * @returns {Promise<Object|null>} Guideline details or null if not found
 */
export const getIreferGuideline = async (ireferCode) => {
  try {
    if (!ireferCode) {
      return null;
    }

    const { data, error } = await supabase?.from('irefer_mappings')?.select('*')?.eq('irefer_code', ireferCode)?.maybeSingle();

    if (error) throw error;
    
    if (!data) {
      return null;
    }
    
    return {
      ireferCode: data?.irefer_code,
      ireferUrl: data?.irefer_url,
      title: data?.title,
      category: data?.category,
      bodyRegion: data?.body_region,
      clinicalScenario: data?.clinical_scenario,
      modality: data?.modality,
      evidenceGrade: data?.evidence_grade,
      keywords: data?.keywords || []
    };
  } catch (error) {
    console.error('Error fetching iRefer guideline:', error);
    return null;
  }
};

/**
 * Search iRefer guidelines
 * @param {Object} filters - Search filters
 * @returns {Promise<Array>} Array of guidelines
 */
export const searchIreferGuidelines = async (filters = {}) => {
  try {
    let query = supabase?.from('irefer_mappings')?.select('*');

    if (filters?.bodyRegion) {
      query = query?.ilike('body_region', `%${filters?.bodyRegion}%`);
    }

    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }

    if (filters?.modality) {
      query = query?.eq('modality', filters?.modality);
    }

    const { data, error } = await query?.order('title');

    if (error) throw error;
    
    if (!data || data?.length === 0) {
      return [];
    }
    
    return data?.map(item => ({
      ireferCode: item?.irefer_code,
      ireferUrl: item?.irefer_url,
      title: item?.title,
      category: item?.category,
      bodyRegion: item?.body_region,
      clinicalScenario: item?.clinical_scenario,
      modality: item?.modality,
      evidenceGrade: item?.evidence_grade,
      keywords: item?.keywords || []
    }));
  } catch (error) {
    console.error('Error searching iRefer guidelines:', error);
    return [];
  }
};

/**
 * Match recommendation to iRefer guideline
 * @param {Object} recommendation - Recommendation data
 * @returns {Promise<Object|null>} Matched guideline or null
 */
export const matchRecommendationToIrefer = async (recommendation) => {
  try {
    const { data, error } = await supabase?.rpc('match_recommendation_to_irefer', {
        p_body_region: recommendation?.bodyRegion || '',
        p_symptoms: recommendation?.clinicalPresentation || '',
        p_clinical_details: recommendation?.reasoning || '',
        p_recommended_modality: recommendation?.recommendedModality || ''
      });

    if (error) throw error;
    
    if (data && data?.length > 0) {
      const match = data?.[0];
      return {
        ireferCode: match?.irefer_code,
        ireferUrl: match?.irefer_url,
        confidence: match?.confidence,
        matchScore: match?.match_score
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error matching recommendation to iRefer:', error);
    return null;
  }
};

// =================================================================
// ACCESS LOGGING
// =================================================================

/**
 * Log iRefer access
 * @param {Object} logData - Access log data
 * @returns {Promise<Object>} Log entry
 */
export const logIreferAccess = async (logData) => {
  try {
    const { data, error } = await supabase?.from('irefer_access_log')?.insert([{
        user_id: logData?.userId,
        recommendation_id: logData?.recommendationId || null,
        irefer_code: logData?.ireferCode,
        access_type: logData?.accessType || 'manual',
        auto_login_success: logData?.autoLoginSuccess || false
      }])?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error logging iRefer access:', error);
    throw error;
  }
};

/**
 * Get user's iRefer access history
 * @param {string} userId - User ID
 * @param {number} limit - Number of records to fetch
 * @returns {Promise<Array>} Access log entries
 */
export const getIreferAccessHistory = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase?.from('irefer_access_log')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;
    
    return data?.map(log => ({
      id: log?.id,
      userId: log?.user_id,
      recommendationId: log?.recommendation_id,
      ireferCode: log?.irefer_code,
      accessType: log?.access_type,
      autoLoginSuccess: log?.auto_login_success,
      createdAt: log?.created_at
    }));
  } catch (error) {
    console.error('Error fetching iRefer access history:', error);
    throw error;
  }
};